<template>
  <div class="tax-return-list">
    <v-progress-circular v-if="loading" indeterminate color="primary" />

    <!-- State: View -->
    <v-card
      v-else
      :raised="false"
      :key="taxReturn.id"
      class="card"
      v-for="taxReturn in currentTaxReturns"
    >
      <v-card-title class="item-title text-xs-left" primary-title pt-0>
        <h2>{{ taxReturn.tax_year }} Tax Return</h2>
      </v-card-title>

      <v-card-text class="item-text text-xs-left py-0 instruction">
        <span
          >To view this tax return <b>{{ taxReturn.tax_return_name }}</b
          >, please click the button below.</span
        >

        <ul class="py-2">
          <li
            v-if="
              taxReturn.status !== 'finished' &&
                getUnsignedFormCount(taxReturn) > 0
            "
          >
            <span>{{ getUnsignedFormCount(taxReturn) }}</span> form{{
              getUnsignedFormCount(taxReturn) > 1 ? 's' : ''
            }}
            to sign.
          </li>

          <li v-if="getUnmailedPaperFiledReturnCount(taxReturn) > 0">
            <span>{{ getUnmailedPaperFiledReturnCount(taxReturn) }}</span>
            paper-filed return{{
              getUnmailedPaperFiledReturnCount(taxReturn) > 1 ? 's' : ''
            }}
            to mail.
          </li>

          <li v-if="getUnpaidPaymentCount(taxReturn) > 0">
            <span>{{ getUnpaidPaymentCount(taxReturn) }}</span> payment{{
              getUnpaidPaymentCount(taxReturn) > 1 ? 's' : ''
            }}
            remaining.
          </li>
        </ul>
      </v-card-text>

      <v-card-actions class="pa-3">
        <v-layout justify-between align-center wrap>
          <v-layout align-center wrap>
            <v-layout align-center wrap>
              <v-btn
                slot="activator"
                :block="$vuetify.breakpoint.xsOnly"
                color="primary"
                class="text-none action-button mr-4"
                @click="goToTaxReturn(taxReturn)"
              >
                View tax return
              </v-btn>
            </v-layout>
          </v-layout>
        </v-layout>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import API from '@/api';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  components: {},

  mixins: [],

  props: {
    currentCompany: {
      type: Object,
    },

    yearFilter: {
      type: Number,
    },
  },

  data() {
    return {
      loading: false,
      currentTaxReturns: [],
    };
  },

  methods: {
    async getTaxReturns() {
      try {
        this.loading = true;

        let params = { companyId: this.currentCompany.id };
        this.isClientDashboardTaxYearFilterEnabled
          ? (params['year'] = this.yearFilter)
          : null;

        const response = await API.taxReturn.getTaxReturns(params);

        // When viewing the dashboard as client,
        // we need to filter out tax returns that are still in progress or require action
        this.currentTaxReturns = response.data.filter(taxReturn => {
          return (
            taxReturn.status !== 'action_required' &&
            taxReturn.status !== 'processing'
          );
        });
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem getting tax returns',
          errorToSend: error,
        });
      } finally {
        this.loading = false;
      }
    },

    goToTaxReturn(taxReturn) {
      this.$router.push({
        name: 'tax-return-delivery',
        params: {
          id: taxReturn.id,
        },
        query: {
          companyId: this.currentCompany.id,
        },
      });
    },

    getUnsignedFormCount(taxReturn) {
      return taxReturn.e_sign_forms
        .filter(form => {
          // Filter out forms that are related to the current user
          return (
            form.signatures &&
            form.signatures.some(signature => {
              return signature.userId === this.currentUser.id;
            })
          );
        })
        .filter(form => {
          if (form.wetSignaturesRequired) {
            return !form.wetSignatures || form.wetSignatures.length === 0;
          } else {
            return form.signatures.some(signature => {
              return (
                signature.userId === this.currentUser.id &&
                !signature.signatureData
              );
            });
          }
        }).length;
    },

    getUnpaidPaymentCount(taxReturn) {
      const hasUnpaidInvoice =
        taxReturn.payment && taxReturn.payment.status !== 'paid';

      return (
        taxReturn.payment_vouchers.filter(paymentVoucher => {
          return !paymentVoucher.is_paid;
        }).length + (hasUnpaidInvoice ? 1 : 0)
      );
    },

    getUnmailedPaperFiledReturnCount(taxReturn) {
      return taxReturn.paper_filed_returns.filter(paperFiledReturn => {
        return !paperFiledReturn.is_mailed;
      }).length;
    },
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
      isClientDashboardTaxYearFilterEnabled:
        'isClientDashboardTaxYearFilterEnabled',
    }),
  },

  async created() {
    await this.getTaxReturns();
  },

  watch: {
    async yearFilter() {
      await this.getTaxReturns();
    },
  },
};
</script>

<style scoped lang="postcss">
.tax-return-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
}

.instruction {
  font-size: 16px;
}
</style>
